import { Box, ChevronDownIcon, HStack, Pressable, Spinner, useTheme } from 'native-base';
import { isEmpty } from 'lodash';
import { ComponentProps, ForwardedRef, forwardRef } from 'react';
import { Plus, Sparkles } from 'lucide-react';

import { CalendarIcon, Lock01Icon, PencilIcon, Text, TextProps, ZapFastIcon } from '@pimm/base';

type PressableSelectProps = {
  _container?: ComponentProps<typeof Pressable>;
  _inputWrapper?: ComponentProps<typeof HStack>;
  _text?: TextProps;
  hideClear?: boolean;
  isAutoAssigned?: boolean;
  isAutoScheduled?: boolean;
  isDisabled?: boolean;
  isCompleted?: boolean;
  isLoading?: boolean;
  isReadOnly?: boolean;
  isDailyCleaning?: boolean;
  colorScheme?: string;
  placeholder?: string;
  value?: string;
  onClear?: () => void;
  onPress?: () => void;
};

export const PressableSelect = forwardRef<typeof Pressable, PressableSelectProps>(
  (
    {
      _container,
      _inputWrapper,
      _text,
      hideClear,
      isAutoAssigned,
      isAutoScheduled,
      isDisabled,
      isCompleted,
      isLoading,
      isReadOnly,
      isDailyCleaning,
      colorScheme,
      placeholder,
      value,
      ...props
    }: PressableSelectProps,
    ref: ForwardedRef<typeof Pressable>,
  ) => {
    const { colors } = useTheme();
    const isBlank = isEmpty(value);

    return (
      <Pressable ref={ref} isDisabled={isDisabled || isCompleted} rounded="lg" onPress={props.onPress} {..._container}>
        <HStack
          rounded="lg"
          alignItems="center"
          justifyContent="space-between"
          pl={2}
          h={8}
          borderWidth={1}
          borderColor="gray.200"
          bg="white"
          {..._inputWrapper}
        >
          <HStack flex={1} alignItems="center" space={1} overflow="hidden">
            {!isLoading && isAutoScheduled && (
              <Sparkles size={14} color={colorScheme ? colors[colorScheme]?.[500] : colors.blueLight[500]} />
            )}
            {!isLoading && isAutoAssigned && (
              <ZapFastIcon size="sm" color={colorScheme ? colors[colorScheme]?.[500] : colors.blueLight[500]} />
            )}
            <Text
              size="md"
              fontWeight={!isBlank ? 500 : 400}
              color={!isBlank ? 'black' : isDisabled ? 'gray.300' : 'gray.600'}
              numberOfLines={1}
              {..._text}
            >
              {value || (placeholder ?? 'cleaning_task:select')}
            </Text>
          </HStack>
          <Box alignItems="center" justifyContent="center" w={7} flexShrink={0}>
            {isLoading && <Spinner boxSize="2" color={'gray.400'} />}
            {!isLoading && isCompleted && !isBlank && <Lock01Icon color="gray.400" />}
            {!isLoading && !isCompleted && !hideClear && !isBlank && !isDisabled && (
              <Box bg="gray.600" rounded="md" p={1} alignItems="center" justifyContent="center">
                <PencilIcon size="xs" color="white" />
              </Box>
            )}
            {!isLoading && !isCompleted && (hideClear || isBlank) && (
              <HStack alignItems="center">
                {hideClear ? (
                  !isDailyCleaning ? (
                    <CalendarIcon color={isReadOnly ? 'gray.200' : 'gray.500'} size="xs" />
                  ) : (
                    <ChevronDownIcon color={isReadOnly ? 'gray.200' : 'gray.500'} size="2xs" />
                  )
                ) : (
                  <Plus size={13} color={isDisabled ? colors.gray[500] : colors.primary[700]} />
                )}
              </HStack>
            )}
          </Box>
        </HStack>
      </Pressable>
    );
  },
);
